import React from 'react';
import { Flex, InputNumber, Tooltip } from 'antd';
import { faSlack } from '@fortawesome/free-brands-svg-icons';
import {
  faCircle,
  faCircleDot,
  faCopy,
  faInfoCircle,
  faRefresh,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clipboardCopy from 'clipboard-copy';

import { SlackTargetingClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { CircledFontAwesomeIcon } from 'jf/components/CircledFontAwesomeIcon';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExLink } from 'jf/components/DevExLink';
import { DevExLoader } from 'jf/components/DevExLoader';
import { DevExText } from 'jf/components/DevExText';
import { ReleaseTag } from 'jf/components/ReleaseTag';
import { getDevExEnv } from 'jf/utils/getDevExEnv';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { useStudyBuilderState } from '../StudyBuilderState';
import { useStudyUpdater } from '../useStudyUpdater';

import { SlackTargetEditor } from './SlackTargetEditor';

export const TargetRespondentStep: React.FC = () => {
  const theme = useDevExTheme();
  const state = useStudyBuilderState();
  const studyUpdater = useStudyUpdater(state.studyRef);

  const isCustomerAdmin = window.dx.user?.isCustomerAdmin;

  const { data: slackStatus } = useClientQuery(SlackTargetingClient.getSlackStatus);

  const devExEnv = getDevExEnv();

  if (!slackStatus) {
    return <DevExLoader />;
  }

  return (
    <Flex
      vertical
      style={{ fontSize: theme.variable.fontSize.md, lineHeight: theme.variable.lineHeight }}
      gap={theme.variable.spacing.lg}
      data-pendo={!slackStatus?.connected ? 'no-integration' : undefined}
    >
      <div style={{ fontSize: theme.variable.fontSize.xl }}>Target Respondents</div>
      <div>Choose how you want to distribute this survey after publishing.</div>

      <DevExCard
        onClick={
          state.slackTargetingEnabled
            ? () => state.update({ slackTargetingEnabled: false })
            : undefined
        }
        title={
          <>
            <FontAwesomeIcon
              icon={!state.slackTargetingEnabled ? faCircleDot : faCircle}
              color={theme.color.brand.active}
            />
            I'll share the link myself
          </>
        }
      >
        {!state.slackTargetingEnabled && (
          <>
            <DevExText>
              Approximately how many respondents will you share the survey with?
            </DevExText>
            <Flex align="center" gap={theme.variable.spacing.sm}>
              <InputNumber
                placeholder="0"
                onChange={(value) => {
                  const newValue = value ? parseInt(value) : null;
                  state.update({ targetResponses: newValue });
                  studyUpdater.update({ targetResponses: newValue });
                }}
                value={state.targetResponses?.toString() ?? ''}
                min={'0'}
              />
              <DevExText fontSize="md">target respondents</DevExText>
            </Flex>
            <DevExText type="secondary" lined>
              <FontAwesomeIcon
                icon={faInfoCircle}
                style={{
                  color: theme.color.text.secondary,
                }}
              />{' '}
              Providing an estimated number of respondents allows you to monitor your survey's
              response rate in real-time as responses come in.
            </DevExText>
          </>
        )}
        {state.slackTargetingEnabled && (
          <DevExText type="secondary" fontSize="md">
            Select this option to manage your survey's distribution yourself.
          </DevExText>
        )}
      </DevExCard>
      <DevExCard
        onClick={
          !state.slackTargetingEnabled
            ? () => state.update({ slackTargetingEnabled: true })
            : undefined
        }
        title={
          <>
            <FontAwesomeIcon
              icon={state.slackTargetingEnabled ? faCircleDot : faCircle}
              color={theme.color.brand.active}
            />
            Distribute in Slack
          </>
        }
        extra={<ReleaseTag stage="BETA" />}
      >
        {state.slackTargetingEnabled ? (
          !slackStatus?.connected ? (
            <Flex justify="center">
              <Flex vertical align="center" gap={theme.variable.spacing.lg}>
                <CircledFontAwesomeIcon
                  icon={faSlack}
                  style={{ color: theme.color.text.tertiary }}
                />
                <Flex
                  vertical
                  gap={theme.variable.spacing.lg}
                  style={{ paddingInline: theme.variable.spacing.lg, textAlign: 'center' }}
                >
                  {isCustomerAdmin ? (
                    <>
                      <DevExText lined>
                        As an admin you can set up the Slack integration directly in{' '}
                        <DevExLink href={`${devExEnv.appOrigin}/settings/plugins`} target="_blank">
                          {'Jellyfish Settings > Integrations'}
                        </DevExLink>
                      </DevExText>
                      <DevExText lined>
                        Once you're done, simply reload this page to start using Slack
                      </DevExText>
                    </>
                  ) : (
                    <>
                      <DevExText lined>
                        Contact your Jellyfish admin to set up your Slack integration in Jellyfish
                        Settings{' > '}Integrations{' '}
                        <Tooltip title="Copy link to clipboard">
                          <FontAwesomeIcon
                            icon={faCopy}
                            onClick={() => {
                              clipboardCopy(`${devExEnv.appOrigin}/settings/plugins`); // Optional: Notify the user
                            }}
                            color={theme.color.brand.active}
                            style={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </DevExText>
                      <DevExText lined>
                        Once they're done, simply reload this page to start using Slack
                      </DevExText>
                    </>
                  )}

                  <Flex justify="center">
                    {isCustomerAdmin ? (
                      <Flex gap={theme.variable.spacing.sm}>
                        <DevExButton
                          type="outline"
                          icon={<FontAwesomeIcon icon={faRefresh} />}
                          onClick={() => window.location.reload()}
                        >
                          Reload
                        </DevExButton>
                        <DevExButton
                          href={`${devExEnv.appOrigin}/settings/plugins`}
                          type="primary"
                          target="_blank"
                        >
                          Go to settings
                        </DevExButton>
                      </Flex>
                    ) : (
                      <DevExButton
                        type="outline"
                        icon={<FontAwesomeIcon icon={faRefresh} />}
                        onClick={() => window.location.reload()}
                      >
                        Reload
                      </DevExButton>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          ) : (
            <>
              <DevExText type="secondary" fontSize="md">
                Select this option to send the survey as a Slack direct message to each respondent.
              </DevExText>
              <SlackTargetEditor />
            </>
          )
        ) : (
          <DevExText type="secondary" fontSize="md" lined>
            Our Slack bot handles your survey distribution seamlessly. Key benefits:
            <ul style={{ margin: 0 }}>
              <li>Ensures an accurate response rate</li>
              <li>Sends reminders only to those who haven't responded yet</li>
            </ul>
          </DevExText>
        )}
      </DevExCard>
    </Flex>
  );
};
